import React, { useCallback, useEffect, useState } from "react";
import { Button, SectionTitle, Spinner, Textarea, toast } from "@porchpass/ui";
import { createNote, getNotes } from "src/api/preApproval";
import type {
  ConditionSection,
  PreApprovalNote,
} from "src/api/preApproval/types";
import { PreApprovalEmptyState } from "../../components/PreApprovalEmptyState";
import { useWorkspace } from "../WorkspaceContext";
import { CreateConditionFromNoteModal } from "./CreateConditionFromNoteModal";
import { EditNoteModal } from "./EditNoteModal";
import { NoteCard } from "./NoteCard";

export function NotesPanel() {
  const { externalId, refreshCounts, bumpConditionsRefresh } = useWorkspace();
  const [notes, setNotes] = useState<PreApprovalNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<PreApprovalNote | null>(null);
  const [promoteNote, setPromoteNote] = useState<PreApprovalNote | null>(null);
  const [promoteSection, setPromoteSection] =
    useState<ConditionSection>("customer");
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadNotes = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const nextNotes = await getNotes(externalId);
      setNotes(nextNotes);
    } catch (error) {
      setNotes([]);
      setLoadError(
        error instanceof Error
          ? error.message
          : "Unable to load notes right now.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [externalId]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes, refreshKey]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) {
      setSubmitError("Enter a note before submitting.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await createNote(externalId, trimmed);
      toast.success("Note added to DigiFi");
      setContent("");
      setRefreshKey((current) => current + 1);
      await refreshCounts();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to add note right now.";
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSuccess = (updated: PreApprovalNote) => {
    setNotes((current) =>
      current.map((note) => (note.id === updated.id ? updated : note)),
    );
    setEditingNote(null);
    void refreshCounts();
  };

  const handlePromote = (note: PreApprovalNote, section: ConditionSection) => {
    setPromoteNote(note);
    setPromoteSection(section);
    setIsPromoteModalOpen(true);
  };

  const handlePromoteSuccess = () => {
    void refreshCounts();
    bumpConditionsRefresh();
  };

  return (
    <div className="flex-1 flex flex-col gap-4 p-6 bg-[#FAFBFC] overflow-y-auto min-h-0">
      <SectionTitle
        size="md"
        title="Notes"
        subtitle="Add context and promote notes to conditions without leaving the workspace."
      />

      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        className="flex flex-col gap-3"
      >
        <Textarea
          label="Add a note about this application..."
          value={content}
          onChange={(event) => {
            setContent(event.target.value);
            if (submitError) {
              setSubmitError(null);
            }
          }}
          rows={3}
          disabled={isSubmitting}
        />
        {submitError && <p className="text-sm text-[#B91C1C]">{submitError}</p>}
        <div className="flex justify-end">
          <Button type="submit" size="s" disabled={isSubmitting}>
            {isSubmitting ? "Adding..." : "Add note"}
          </Button>
        </div>
      </form>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : loadError ? (
        <PreApprovalEmptyState
          title="Unable to load notes"
          description={loadError}
        />
      ) : notes.length === 0 ? (
        <PreApprovalEmptyState
          title="No notes yet"
          description="Add the first note using the form above."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              onEdit={() => setEditingNote(note)}
              onPromote={(section) => handlePromote(note, section)}
            />
          ))}
        </div>
      )}

      {editingNote && (
        <EditNoteModal
          note={editingNote}
          externalId={externalId}
          onCancel={() => setEditingNote(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      <CreateConditionFromNoteModal
        note={promoteNote}
        initialSection={promoteSection}
        open={isPromoteModalOpen}
        onOpenChange={setIsPromoteModalOpen}
        onSuccess={handlePromoteSuccess}
      />
    </div>
  );
}
